import React, { useState } from 'react';
import {
  CreateRecipeData, Material, EQUIPMENT_SLOTS, VISIBLE_EQUIPMENT_SLOTS, SLOT_TO_SPRITE_CATEGORY,
} from '../types/adminContent';
import PlayerEquipSpritePicker from './PlayerEquipSpritePicker';

interface Props {
  title: string;
  submitLabel: string;
  formData: CreateRecipeData;
  materials: Material[];
  onChange: (data: CreateRecipeData) => void;
  onSubmit: () => void;
  onClose: () => void;
}

const ITEM_TYPES = ['Weapon', 'Armor', 'Accessory', 'Consumable', 'Material', 'Tool'];

const CreateRecipeModal: React.FC<Props> = ({ title, submitLabel, formData, materials, onChange, onSubmit, onClose }) => {
  const [showSpritePicker, setShowSpritePicker] = useState(false);
  const [ingredientMaterialId, setIngredientMaterialId] = useState<number | ''>('');
  const [ingredientQuantity, setIngredientQuantity] = useState(1);

  const isVisibleSlot = VISIBLE_EQUIPMENT_SLOTS.some(s => s === formData.equipmentSlot);
  const spriteCategory = formData.equipmentSlot ? SLOT_TO_SPRITE_CATEGORY[formData.equipmentSlot] : undefined;

  const addIngredient = () => {
    if (ingredientMaterialId === '' || ingredientQuantity < 1) return;
    if (formData.ingredients.some(i => i.materialId === ingredientMaterialId)) return;
    onChange({
      ...formData,
      ingredients: [...formData.ingredients, { materialId: ingredientMaterialId, quantity: ingredientQuantity }],
    });
    setIngredientMaterialId('');
    setIngredientQuantity(1);
  };

  const removeIngredient = (materialId: number) => {
    onChange({ ...formData, ingredients: formData.ingredients.filter(i => i.materialId !== materialId) });
  };

  const materialName = (id: number) => materials.find(m => m.id === id)?.name ?? `#${id}`;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>✨ {title}</h3>
          <button onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <div className="form-grid">
            <div className="form-group">
              <label>Recipe Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={e => onChange({ ...formData, name: e.target.value })}
                placeholder="e.g., Iron Sword"
              />
            </div>

            <div className="form-group">
              <label>Item Type *</label>
              <select
                value={formData.category}
                onChange={e => onChange({ ...formData, category: e.target.value })}
              >
                {ITEM_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>

            <div className="form-group">
              <label>Equipment Slot</label>
              <select
                value={formData.equipmentSlot ?? ''}
                onChange={e => onChange({
                  ...formData,
                  equipmentSlot: e.target.value || null,
                  equipSpritePath: e.target.value ? formData.equipSpritePath : null,
                })}
              >
                <option value="">Not equippable</option>
                {EQUIPMENT_SLOTS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>

            <div className="form-group">
              <label>Required Level</label>
              <input
                type="number"
                min="1"
                value={formData.requiredLevel}
                onChange={e => onChange({ ...formData, requiredLevel: parseInt(e.target.value) || 1 })}
              />
            </div>

            <div className="form-group">
              <label>Crafting Time (s)</label>
              <input
                type="number"
                min="1"
                value={formData.craftingTime}
                onChange={e => onChange({ ...formData, craftingTime: parseInt(e.target.value) || 1 })}
              />
            </div>

            {isVisibleSlot && (
              <div className="form-group full-width">
                <label>Equip Sprite (battlefield overlay)</label>
                <div className="monster-sprite-field">
                  {formData.equipSpritePath && (
                    <img className="monster-sprite-preview" src={formData.equipSpritePath} alt="Selected equip sprite" />
                  )}
                  <button
                    type="button"
                    className="sprite-picker-button"
                    onClick={() => setShowSpritePicker(true)}
                  >
                    🖼️ {formData.equipSpritePath ? 'Change Sprite' : 'Choose Sprite'}
                  </button>
                  {formData.equipSpritePath && (
                    <button
                      type="button"
                      className="sprite-picker-button"
                      onClick={() => onChange({ ...formData, equipSpritePath: null })}
                    >
                      Clear
                    </button>
                  )}
                </div>
              </div>
            )}

            <div className="form-group full-width">
              <label>Description</label>
              <textarea
                value={formData.description}
                onChange={e => onChange({ ...formData, description: e.target.value })}
                placeholder="Describe this recipe..."
                rows={3}
              />
            </div>

            <div className="form-group full-width">
              <label>Ingredients</label>
              <div className="recipe-ingredient-adder">
                <select
                  value={ingredientMaterialId}
                  onChange={e => setIngredientMaterialId(e.target.value ? Number(e.target.value) : '')}
                >
                  <option value="">Select material...</option>
                  {materials.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
                <input
                  type="number"
                  min="1"
                  value={ingredientQuantity}
                  onChange={e => setIngredientQuantity(parseInt(e.target.value) || 1)}
                />
                <button type="button" className="sprite-picker-button" onClick={addIngredient}>+ Add</button>
              </div>
              <ul className="recipe-ingredient-list">
                {formData.ingredients.map(i => (
                  <li key={i.materialId}>
                    <span>{i.quantity}x {materialName(i.materialId)}</span>
                    <button type="button" onClick={() => removeIngredient(i.materialId)}>×</button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="cancel-button" onClick={onClose}>Cancel</button>
          <button
            className="create-button"
            onClick={onSubmit}
            disabled={!formData.name}
          >
            ✨ {submitLabel}
          </button>
        </div>
      </div>

      {showSpritePicker && spriteCategory && (
        <PlayerEquipSpritePicker
          category={spriteCategory}
          slotLabel={formData.equipmentSlot ?? ''}
          onSelect={url => onChange({ ...formData, equipSpritePath: url })}
          onClose={() => setShowSpritePicker(false)}
        />
      )}
    </div>
  );
};

export default CreateRecipeModal;
